import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import { Router } from '@angular/router';
import { Organization } from '../organization/organization';
import { PersonLogin, RoleName } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { OrganizationService } from '../services/organization-service';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { SelectionService } from '../services/selection-service';
import { StringUtils } from '../utils/string-utils';
import { Person } from './person';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { DatePipe } from '@angular/common';
import { Observable, firstValueFrom, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-person-detail',
  templateUrl: './person-detail.component.html',
  styleUrls: ['./person-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, FormsModule, DatePipe],
})
export class PersonDetailComponent implements OnInit {
  readonly personId = input.required<string>({ alias: 'person-id' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly personService = inject(PersonService);
  private readonly organizationService = inject(OrganizationService);
  private readonly selectionService = inject(SelectionService);
  private readonly router = inject(Router);
  personLogin!: PersonLogin;
  person: Person;
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  selectedId!: string;
  primaryOrgUnit: Organization;
  userCustomerOrgUnit: Organization;
  userVendorOrgUnit: Organization;
  controllingOrgs!: SelectionItem[];
  regions!: SelectionItem[];
  repOffices!: SelectionItem[];
  personTypes!: SelectionItem[];
  userTypes!: SelectionItem[];
  personStatusCodes!: SelectionItem[];
  availablePartsSalesManagers!: SelectionItem[];
  availableUnitSalesManagers!: SelectionItem[];
  readonly targetLogin = signal<PersonLogin | undefined>(undefined);
  readonly loginValue = signal<string>('');

  constructor() {
    this.person = new Person();
    this.primaryOrgUnit = new Organization();
    this.userCustomerOrgUnit = new Organization();
    this.userVendorOrgUnit = new Organization();
  }

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin || this.personLoginService.hasRole(RoleName.LSBCS)) {
      this.router.navigate(['main']);
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.initialize().then(
        () => (this.isBusy.set(false)),
        (error) => this.handleError(error),
      );
    }
  }

  private async initialize(): Promise<void> {
    const personId: string = this.personId();
    if (personId && personId.length > 0) {
      if (personId === 'new') {
        if (this.personLoginService.hasRole(RoleName.IECIT)) {
          await this.loadSelectionLists();
          await this.loadOrganizations();
          this.startNewPerson();
        } else {
          this.backToPersonSearch();
        }
      } else {
        this.person = await firstValueFrom<Person>(this.personService.getPersonById(personId));
        if (this.personLoginService.hasRole(RoleName.IECIT) || (this.person && this.person.primaryOrgUnitId && this.person.primaryOrgUnitId === this.personLogin.person.primaryOrgUnitId)) {
          await this.loadSelectionLists();
          await this.loadOrganizations();
          if (this.personLoginService.isAdmin()) {
            const targetLogin = await firstValueFrom<PersonLogin>(this.personLoginService.getPersonLogin(personId));
            this.targetLogin.set(targetLogin ?? undefined);
            this.loginValue.set(targetLogin?.googleId ?? '');
          }
        } else {
          this.backToPersonSearch();
        }
      }
    } else {
      this.backToPersonSearch();
    }
  }

  public async loadOrganizations(): Promise<void> {
    if (!this.hasRole(RoleName.IECIT)) {
      this.primaryOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.person.primaryOrgUnitId));
      this.userCustomerOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.person.userCustomerOrgId));
      this.userVendorOrgUnit = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.person.userVendorOrgId));
    } else {
      this.controllingOrgs = await firstValueFrom<SelectionItem[]>(this.selectionService.getControllingOrgs());
      this.regions = await firstValueFrom<SelectionItem[]>(this.selectionService.getActiveRegionOrgs());
      this.repOffices = await firstValueFrom<SelectionItem[]>(this.selectionService.getActiveRepOffices());
    }
  }

  public hasRole(roleName: string) {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  public isNewPerson() {
    return !this.person || !this.person.personId || this.person.personId.length === 0;
  }

  public isRolesMaintainable() {
    return this.person && this.person.personId && this.person.personId.length > 0;
  }

  public backToPersonSearch(): void {
    this.router.navigate(['person']);
  }

  private async loadSelectionLists(): Promise<void> {
    this.personTypes = await firstValueFrom<SelectionItem[]>(this.selectionService.getPersonTypes());
    this.userTypes = await firstValueFrom<SelectionItem[]>(this.selectionService.getUserTypes());
    this.personStatusCodes = await firstValueFrom<SelectionItem[]>(this.selectionService.getPersonStatusCodes());
    let userList: SelectionItem[] = await firstValueFrom<SelectionItem[]>(this.selectionService.getSalesManagers());
    userList = userList.filter((user) => StringUtils.isNotBlank(user.description)).sort((first, second) => first.description.localeCompare(second.description));
    this.availablePartsSalesManagers = JSON.parse(JSON.stringify(userList));
    this.availableUnitSalesManagers = JSON.parse(JSON.stringify(userList));
  }

  private startNewPerson() {
    this.person.active = true;
    this.person.statusCode = 'ACTV';
  }

  public manageRoles() {
    this.router.navigate(['person/roles/' + this.person.personId]);
  }

  public manageGroups() {
    this.router.navigate(['person/groups/' + this.person.personId]);
  }

  public manageDelegations() {
    this.router.navigate(['person/delegations/' + this.person.personId]);
  }

  public onLoginInput(event: Event): void {
    this.loginValue.set((event.target as HTMLInputElement).value);
  }

  private saveLoginIfChanged(): Observable<PersonLogin | undefined> {
    const existing = this.targetLogin();
    const googleId = this.loginValue().trim();
    if (existing && StringUtils.isNotBlank(googleId) && googleId !== existing.googleId) {
      return this.personLoginService.savePersonLogin({ ...existing, googleId }).pipe(
        tap((result: PersonLogin) => {
          this.targetLogin.set(result);
          this.loginValue.set(result.googleId);
        }),
      );
    }
    return of(undefined);
  }

  public savePerson(): void {
    if (!this.person.primaryOrgUnitId || this.person.primaryOrgUnitId.length === 0) {
      this.errorMessage.set('Please select a Primary Org');
    } else if (!this.person.userCustomerOrgId || this.person.userCustomerOrgId.length === 0) {
      this.errorMessage.set('Please select a Region');
    } else if (!this.person.userVendorOrgId || this.person.userVendorOrgId.length === 0) {
      this.errorMessage.set('Please select a Parent Office');
    } else if (!this.person.firstName || this.person.firstName.length === 0) {
      this.errorMessage.set('Please enter a first name');
    } else if (!this.person.lastName || this.person.lastName.length === 0) {
      this.errorMessage.set('Please enter a last name');
    } else {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      if (this.isNewPerson()) {
        this.person.createUser = this.personLogin.person.userLogin;
        this.person.createTimestamp = new Date();
      }
      if (!StringUtils.equalsIgnoreCase('REGMGR', this.person.typeCode)) {
        this.person.largeTextUserField2 = '';
        this.person.largeTextUserField4 = '';
      }
      this.person.updateUser = this.personLogin.person.userLogin;
      this.person.updateTimestamp = new Date();
      this.person.description = this.person.firstName + ' ' + this.person.lastName;
      this.saveLoginIfChanged().pipe(switchMap(() => this.personService.save(this.person))).subscribe({
        next: (result: Person) => {
          this.isBusy.set(false);
          this.person = result;
        }, error: (error: HttpErrorResponse) => this.handleError(error)
      });
    }
  }

  private handleError(error: HttpErrorResponse) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  salesManagerInputDisabled(): boolean {
    return this.isBusy() || !StringUtils.equalsIgnoreCase('REGMGR', this.person.typeCode) || !this.personLoginService.isAdmin();
  }

}
